/**
 * Pure formatter helpers for Avalara sync result → toast severity/content.
 *
 * Returns a plain object { severity, summary, detail } so callers can choose
 * the appropriate toast helper (warnToast / successToast) from @/util/toasts.
 */
import type { AvalaraCompanySyncResult } from '@/types/avalaraSync'

export interface SyncToastContent {
  severity: 'success' | 'warn'
  summary: string
  detail: string
}

/**
 * Format a company sync result into toast content.
 * - Full success (nexusCompaniesFailed === 0): severity=success, detail with counts.
 * - Partial success (nexusCompaniesFailed > 0): severity=warn, detail lists failed company codes
 *   when there are 5 or fewer failures; otherwise it shows the failed count and
 *   a "see server logs" message instead of enumerating codes.
 */
export function formatCompanySyncToast(result: AvalaraCompanySyncResult): SyncToastContent {
  const { added, updated, deleted, nexusCompaniesFailed, nexusFailures } = result

  if (nexusCompaniesFailed === 0) {
    return {
      severity: 'success',
      summary: 'Companies synced',
      detail: `${added} added, ${updated} updated, ${deleted} deleted`,
    }
  }

  const countDetail = `${added} added, ${updated} updated, ${deleted} deleted`

  let failureDetail: string
  if (nexusFailures.length > 5) {
    failureDetail = `${nexusFailures.length} companies failed nexus refresh; see server logs`
  } else {
    // The companies table shows codes, not Avalara IDs. A failure without a code
    // still names its company by ID rather than dropping out of the list.
    const companies = nexusFailures
      .map(f => f.companyCode || String(f.companyId))
      .join(', ')
    failureDetail = `Nexus refresh failed for companies: ${companies}`
  }

  return {
    severity: 'warn',
    summary: 'Companies synced with warnings',
    detail: `${countDetail}. ${failureDetail}`,
  }
}
